import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Copy,
  FileDown,
  Plus,
  Send,
  Trash2,
  Ban,
  ReceiptText,
} from "lucide-react";
import { computeInvoiceTotals } from "@mavatech/core";
import {
  DEFAULT_HANDLER_NAME,
  formatDate,
  formatMoney,
} from "@mavatech/shared";
import { useUnsavedGuard } from "@/lib/useUnsavedGuard";
import { PageHeader } from "@/components/layout/Layout";
import {
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  Input,
  Select,
  Textarea,
} from "@/components/ui";
import { CustomerCombobox } from "@/components/customers/CustomerCombobox";
import { PositionEditor } from "@/components/positions/PositionEditor";
import { toItemsPayload, type Row } from "@/components/positions/types";
import { trpc } from "@/lib/trpc";
import { useAppStore } from "@/stores/app.store";
import { CreditNoteDialog } from "./CreditNoteDialog";

const TONE: Record<
  string,
  "neutral" | "accent" | "success" | "warning" | "danger"
> = {
  draft: "neutral",
  sent: "accent",
  partially_paid: "warning",
  paid: "success",
  overdue: "danger",
  cancelled: "neutral",
};
const LABEL: Record<string, string> = {
  draft: "Entwurf",
  sent: "Offen",
  partially_paid: "Teilbezahlt",
  paid: "Bezahlt",
  overdue: "Überfällig",
  cancelled: "Storniert",
};

interface Sched {
  label: string;
  basis: "percent" | "fixed";
  value: number;
  note: string;
}

const iso = (d?: string | Date | null) =>
  d ? new Date(d).toISOString().slice(0, 10) : "";

/** Pro Rechnung eine frische Maske — sonst bleibt beim Wechsel (z. B. von der Rechnung zur neu angelegten
 * Gutschrift) der alte Formularzustand stehen und ein Speichern würde die falschen Daten überschreiben. */
export function InvoiceForm() {
  const { id } = useParams();
  return <InvoiceFormInner key={id ?? "new"} />;
}

function InvoiceFormInner() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const addToast = useAppStore((s) => s.addToast);

  const org = trpc.organization.get.useQuery();
  const members = trpc.auth.members.useQuery();
  const me = trpc.auth.me.useQuery();
  const stdTexts = trpc.standardText.list.useQuery(undefined, {
    enabled: isNew,
  });
  const existing = trpc.invoice.get.useQuery(
    { id: id! },
    { enabled: !isNew, retry: false },
  );

  const [loaded, setLoaded] = useState(false);
  const [customerId, setCustomerId] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [showSiteAddress, setShowSiteAddress] = useState(false);
  const properties = trpc.property.list.useQuery(
    { customerId },
    { enabled: !!customerId },
  );
  // Gutschriften entstehen nur noch aus einer Rechnung (Button Gutschrift erstellen) — nie frei.
  const kind = "standalone" as const;
  const [creditReason, setCreditReason] = useState("");
  const [creditOpen, setCreditOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [refundAmount, setRefundAmount] = useState("");
  const [orderId, setOrderId] = useState("");
  const orders = trpc.order.list.useQuery(
    { customerId },
    { enabled: !!customerId },
  );
  const [title, setTitle] = useState("");
  const [issueDate, setIssueDate] = useState(iso(new Date()));
  const [dueDate, setDueDate] = useState("");
  const [serviceMode, setServiceMode] = useState<"date" | "period">("date");
  const [serviceDate, setServiceDate] = useState(iso(new Date()));
  const [periodFrom, setPeriodFrom] = useState("");
  const [periodTo, setPeriodTo] = useState("");
  const [taxRate, setTaxRate] = useState(20);
  const [rcBau, setRcBau] = useState(false);
  const [introText, setIntroText] = useState("");
  const [infoText, setInfoText] = useState("");
  const [closingText, setClosingText] = useState("");
  const [legalNoteText, setLegalNoteText] = useState("");
  const [courtVenueText, setCourtVenueText] = useState("");
  const [skontoPercent, setSkontoPercent] = useState(0);
  const [skontoDays, setSkontoDays] = useState(8);
  const [paymentGraceDays, setPaymentGraceDays] = useState(14);
  const [handlerUserId, setHandlerUserId] = useState("");
  const [rows, setRows] = useState<Row[]>([]);
  const [sched, setSched] = useState<Sched[]>([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  // Zahlungseingang-Formular
  const [payDate, setPayDate] = useState(iso(new Date()));
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("transfer");
  const [paySkonto, setPaySkonto] = useState(false);

  useEffect(() => {
    if (!isNew || loaded) return;
    if (org.data) {
      setTaxRate((org.data as any).defaultTaxRate ?? 20);
      const days = Number((org.data as any).paymentTermsDays ?? 14) || 14;
      setDueDate(iso(new Date(Date.now() + days * 864e5)));
    }
    if (stdTexts.data) {
      const pick = (s: string) =>
        stdTexts.data!.find((t) => t.scope === s && t.isDefault)?.body ?? "";
      setIntroText(pick("invoice_intro"));
      setClosingText(pick("invoice_closing"));
      setLegalNoteText(pick("doc_legal_note"));
      setCourtVenueText(pick("doc_court_venue"));
      setLoaded(true);
    }
  }, [isNew, loaded, org.data, stdTexts.data]);

  // SachbearbeiterIn vorbelegen: Hauptnutzer (Ratko Mirkovic), sonst angemeldeter
  // Benutzer, sonst erster im Team.
  useEffect(() => {
    if (!isNew || handlerUserId || !members.data) return;
    const preferred =
      members.data.find(
        (m) =>
          m.name.trim().toLowerCase() === DEFAULT_HANDLER_NAME.toLowerCase(),
      )?.userId ??
      me.data?.user.id ??
      members.data[0]?.userId ??
      "";
    if (preferred) setHandlerUserId(preferred);
  }, [isNew, handlerUserId, members.data, me.data]);

  useEffect(() => {
    if (isNew || !existing.data || loaded) return;
    const inv = existing.data;
    setCustomerId(inv.customer.id);
    setPropertyId(inv.propertyId ?? "");
    setShowSiteAddress(!!inv.showSiteAddress);
    setOrderId(inv.orderId ?? "");
    setTitle(inv.title);
    setIssueDate(iso(inv.issueDate));
    setDueDate(iso(inv.dueDate));
    setServiceDate(iso(inv.serviceDate));
    setPeriodFrom(iso(inv.servicePeriodFrom));
    setPeriodTo(iso(inv.servicePeriodTo));
    setServiceMode(
      inv.servicePeriodFrom && inv.servicePeriodTo ? "period" : "date",
    );
    setHandlerUserId(inv.handlerUserId ?? "");
    setCreditReason(inv.creditReason ?? "");
    setTaxRate(inv.taxRatePercent);
    setRcBau(inv.reverseChargeBau);
    setIntroText(inv.introText ?? "");
    setInfoText(inv.infoText ?? "");
    setClosingText(inv.closingText ?? "");
    setLegalNoteText(inv.legalNoteText ?? "");
    setCourtVenueText(inv.courtVenueText ?? "");
    setSkontoPercent(inv.skontoPercent ?? 0);
    setSkontoDays(inv.skontoDays ?? 8);
    setPaymentGraceDays(inv.paymentGraceDays ?? 14);
    setRows(
      inv.items.map((it) => ({
        clientId: it.id,
        parentClientId: it.parentId,
        kind: it.kind as Row["kind"],
        mode: "mandatory",
        itemCategory: (it.itemCategory as Row["itemCategory"]) ?? "material",
        description: it.description,
        longText: it.longText ?? "",
        quantity: it.quantity,
        unit: it.unit,
        unitPriceCents: it.unitPriceCents,
        discountPercent: it.discountPercent,
        alternativeGroup: "",
        isChosenAlternative: false,
        rangeFromLabel: it.rangeFromLabel ?? "",
        rangeToLabel: it.rangeToLabel ?? "",
        articleId: it.articleId ?? null,
        creditedItemId: it.creditedItemId ?? null,
        purchasePriceCents: it.purchasePriceCents ?? null,
      })),
    );
    setSched(
      inv.scheduleLines.map((s) => ({
        label: s.label,
        basis: s.basis as "percent" | "fixed",
        value: s.value,
        note: s.note ?? "",
      })),
    );
    setLoaded(true);
  }, [isNew, existing.data, loaded]);

  const totals = useMemo(
    () =>
      computeInvoiceTotals(
        rows.map((r, i) => ({
          id: r.clientId,
          parentId: r.parentClientId,
          sortIndex: i,
          kind: r.kind,
          quantity: r.quantity,
          unitPriceCents: r.unitPriceCents,
          discountPercent: r.discountPercent,
          mode: "mandatory" as const,
          isChosenAlternative: false,
          rangeFromLabel: r.rangeFromLabel || null,
          rangeToLabel: r.rangeToLabel || null,
        })),
        {
          taxScheme: ((org.data as any)?.taxScheme ?? "regel") as
            "regel" | "kleinunternehmer",
          taxRatePercent: taxRate,
          reverseChargeBau: rcBau,
        },
      ),
    [rows, taxRate, rcBau, org.data],
  );

  const materialCostCents = useMemo(
    () =>
      rows.reduce(
        (sum, r) =>
          r.kind === "position" &&
          r.itemCategory === "material" &&
          r.purchasePriceCents != null
            ? sum + Math.round(r.purchasePriceCents * r.quantity)
            : sum,
        0,
      ),
    [rows],
  );

  const header = () => ({
    customerId,
    propertyId: propertyId || null,
    showSiteAddress: !!propertyId && showSiteAddress,
    orderId: orderId || null,
    title: title.trim(),
    issueDate: new Date(issueDate),
    dueDate: new Date(dueDate || issueDate),
    serviceDate:
      serviceMode === "date" && serviceDate ? new Date(serviceDate) : null,
    servicePeriodFrom:
      serviceMode === "period" && periodFrom ? new Date(periodFrom) : null,
    servicePeriodTo:
      serviceMode === "period" && periodTo ? new Date(periodTo) : null,
    taxRatePercent: taxRate,
    reverseChargeBau: rcBau,
    handlerUserId: handlerUserId || null,
    introText: introText.trim() || null,
    infoText: infoText.trim() || null,
    closingText: closingText.trim() || null,
    legalNoteText: legalNoteText.trim() || null,
    courtVenueText: courtVenueText.trim() || null,
    skontoPercent,
    skontoDays,
    paymentGraceDays,
  });

  // Warnung bei ungespeicherten Eingaben (Zurück, Menü, Seite schließen …), siehe lib/useUnsavedGuard.tsx
  const guard = useUnsavedGuard({
    ready: loaded && (!isNew || !!handlerUserId),
    getSnapshot: () => JSON.stringify([header(), rows, sched, creditReason]),
  });
  const leave = (to: string) => {
    guard.allowLeave();
    navigate(to);
  };

  const create = trpc.invoice.create.useMutation({
    onSuccess: (inv) => {
      utils.invoice.list.invalidate();
      addToast({ type: "success", title: "Rechnung erstellt" });
      leave(`/rechnungen/${inv!.id}`);
    },
    onError: (e) =>
      addToast({ type: "error", title: "Fehler", description: e.message }),
  });
  const update = trpc.invoice.update.useMutation({
    onSuccess: () => {
      guard.markSaved();
      utils.invoice.get.invalidate({ id: id! });
      utils.invoice.list.invalidate();
      addToast({ type: "success", title: "Gespeichert" });
    },
    onError: (e) =>
      addToast({ type: "error", title: "Fehler", description: e.message }),
  });
  const send = trpc.invoice.send.useMutation({
    onSuccess: () => {
      utils.invoice.get.invalidate({ id: id! });
      utils.invoice.list.invalidate();
      addToast({ type: "success", title: "Rechnungsnummer vergeben" });
    },
    onError: (e) =>
      addToast({ type: "error", title: "Fehler", description: e.message }),
  });
  const cancel = trpc.invoice.cancel.useMutation({
    onSuccess: (o) => {
      utils.invoice.get.invalidate({ id: id! });
      utils.invoice.list.invalidate();
      setConfirmCancel(false);
      setCancelReason("");
      const gs = o?.creditNotes?.find(
        (c) => c.number && c.status !== "cancelled",
      );
      addToast({
        type: "info",
        title: "Rechnung storniert",
        description: gs ? "Storno-Gutschrift wurde ausgestellt." : undefined,
      });
    },
    onError: (e) =>
      addToast({
        type: "error",
        title: "Stornieren nicht möglich",
        description: e.message,
      }),
  });
  const refund = trpc.invoice.addRefund.useMutation({
    onSuccess: () => {
      utils.invoice.get.invalidate({ id: id! });
      utils.invoice.list.invalidate();
      setRefundAmount("");
      addToast({ type: "success", title: "Erstattung erfasst" });
    },
    onError: (e) =>
      addToast({ type: "error", title: "Fehler", description: e.message }),
  });
  const remove = trpc.invoice.remove.useMutation({
    onSuccess: () => {
      utils.invoice.list.invalidate();
      leave("/rechnungen");
    },
    onError: (e) =>
      addToast({ type: "error", title: "Fehler", description: e.message }),
  });
  const addPayment = trpc.invoice.addPayment.useMutation({
    onSuccess: () => {
      utils.invoice.get.invalidate({ id: id! });
      utils.invoice.list.invalidate();
      setPayAmount("");
      addToast({ type: "success", title: "Zahlung erfasst" });
    },
    onError: (e) =>
      addToast({ type: "error", title: "Fehler", description: e.message }),
  });
  const addPaymentSkonto = trpc.invoice.addPaymentWithSkonto.useMutation({
    onSuccess: () => {
      utils.invoice.get.invalidate({ id: id! });
      utils.invoice.list.invalidate();
      setPayAmount("");
      setPaySkonto(false);
      addToast({
        type: "success",
        title: "Zahlung mit Skonto erfasst — Rechnung ist damit beglichen",
      });
    },
    onError: (e) =>
      addToast({ type: "error", title: "Fehler", description: e.message }),
  });
  const removePayment = trpc.invoice.removePayment.useMutation({
    onSuccess: () => {
      utils.invoice.get.invalidate({ id: id! });
      utils.invoice.list.invalidate();
    },
  });
  const pdf = trpc.invoice.generatePdf.useMutation({
    onSuccess: (r) => window.open(r.url, "_blank"),
    onError: (e) =>
      addToast({
        type: "error",
        title: "PDF fehlgeschlagen",
        description: e.message,
      }),
  });
  const toQuote = trpc.quote.createFromInvoice.useMutation({
    onSuccess: (q) => {
      utils.quote.list.invalidate();
      addToast({ type: "success", title: "Neues Angebot erstellt" });
      leave(`/angebote/${q.id}`);
    },
    onError: (e) =>
      addToast({ type: "error", title: "Fehler", description: e.message }),
  });
  // E-Mail-Versand (SMTP/.eml) bewusst ausgebaut, bis der Versand richtig angebunden ist (siehe
  // BACKLOG.md) — bis dahin gibt es hier nur den reinen PDF-Download.

  const inv = existing.data;
  const status = inv?.status ?? "draft";
  const locked = status === "paid" || status === "cancelled";
  const canSave = customerId && title.trim();
  const payable = inv
    ? inv.kind === "credit_note"
      ? inv.grossCents
      : inv.payableGrossCents
    : totals.grossCents;
  const open = inv ? Math.max(0, payable - inv.paidCents) : payable;

  const onSave = () => {
    if (!canSave) return false;
    const items = toItemsPayload(rows);
    const scheduleLines = sched
      .filter((s) => s.label.trim())
      .map((s) => ({
        label: s.label.trim(),
        basis: s.basis,
        value: Math.round(s.value),
        note: s.note.trim() || null,
      }));
    if (isNew) create.mutate({ ...header(), items, kind });
    else
      update.mutate({
        id: id!,
        ...header(),
        items,
        scheduleLines,
        creditReason: creditReason.trim() || null,
      });
    return true;
  };
  guard.setSave(onSave);

  // Solange ein bestehender Beleg noch lädt, kein leeres "Entwurf"-Formular zeigen (flackert
  // sonst kurz auf, siehe AUDIT-2026-10-01-UX.md, Punkt 1.4).
  if (!isNew && existing.isLoading) {
    return (
      <div className="flex flex-col h-full">
        <PageHeader title="Rechnung" />
        <div className="flex-1 flex items-center justify-center text-sm text-text-tertiary">
          Lädt …
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" {...guard.rootProps}>
      {guard.dialog}
      <PageHeader
        title={
          isNew
            ? "Neue Rechnung"
            : inv?.kind === "credit_note"
              ? inv.number
                ? `Gutschrift ${inv.number}`
                : "Gutschrift (Entwurf)"
              : inv?.number
                ? `Rechnung ${inv.number}`
                : "Rechnung (Entwurf)"
        }
        actions={
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {!isNew && (
              <Badge tone={TONE[status] ?? "neutral"}>
                {inv?.kind === "credit_note" && status === "paid"
                  ? "Ausgestellt"
                  : (LABEL[status] ?? status)}
              </Badge>
            )}
            <Button variant="ghost" onClick={() => navigate("/rechnungen")}>
              <ArrowLeft className="w-4 h-4" /> Rechnungen
            </Button>
            {!isNew && (
              <>
                <Button
                  variant="ghost"
                  onClick={() => pdf.mutate({ id: id! })}
                  loading={pdf.isPending}
                >
                  <FileDown className="w-4 h-4" /> Download
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => toQuote.mutate({ invoiceId: id! })}
                  loading={toQuote.isPending}
                >
                  <Copy className="w-4 h-4" /> In neues Angebot übernehmen
                </Button>
                {(status === "draft" ||
                  status === "overdue" ||
                  status === "sent") && (
                  <Button
                    variant="secondary"
                    onClick={() => send.mutate({ id: id! })}
                    loading={send.isPending}
                  >
                    <Send className="w-4 h-4" />{" "}
                    {inv?.number ? "Aktualisieren" : "Finalisieren"}
                  </Button>
                )}
                {inv?.creditable && (
                  <Button variant="ghost" onClick={() => setCreditOpen(true)}>
                    <ReceiptText className="w-4 h-4" /> Gutschrift erstellen
                  </Button>
                )}
                {status !== "cancelled" &&
                  inv?.number &&
                  inv.kind !== "credit_note" && (
                    <Button
                      variant="ghost"
                      onClick={() => setConfirmCancel(true)}
                    >
                      <Ban className="w-4 h-4" /> Stornieren
                    </Button>
                  )}
              </>
            )}
            {!locked && (
              <Button
                variant="primary"
                onClick={onSave}
                loading={create.isPending || update.isPending}
                disabled={!canSave}
              >
                Speichern
              </Button>
            )}
            {!isNew && !inv?.number && (
              <Button variant="danger" onClick={() => setConfirmDelete(true)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        }
      />

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {inv?.kind === "credit_note" && (
          <div className="max-w-5xl card p-4 space-y-3">
            <div className="text-sm text-text-secondary">
              Gutschrift zu{" "}
              {inv.creditedInvoice ? (
                <button
                  className="text-accent hover:underline font-medium"
                  onClick={() =>
                    navigate(`/rechnungen/${inv.creditedInvoice!.id}`)
                  }
                >
                  Rechnung {inv.creditedInvoice.number}
                </button>
              ) : (
                <span>—</span>
              )}
              . Die Rechnung selbst bleibt unverändert; die Gutschrift mindert
              deren offenen Betrag.
            </div>
            <Textarea
              label="Grund (erscheint auf der Gutschrift)"
              rows={2}
              value={creditReason}
              disabled={locked}
              onChange={(e) => setCreditReason(e.target.value)}
            />
          </div>
        )}
        {inv &&
          inv.kind !== "credit_note" &&
          (inv.creditNotes.length > 0 || status === "cancelled") && (
            <div className="max-w-5xl card p-4 space-y-3">
              <h2 className="text-sm font-semibold text-text-primary">
                Gutschriften zu dieser Rechnung
              </h2>
              {inv.creditNotes.length ? (
                <div className="space-y-1.5">
                  {inv.creditNotes.map((c) => (
                    <div
                      key={c.id}
                      className="flex items-center justify-between text-sm"
                    >
                      <button
                        className="text-accent hover:underline"
                        onClick={() => navigate(`/rechnungen/${c.id}`)}
                      >
                        {c.number ?? "Gutschrift (Entwurf)"}
                      </button>
                      <span className="text-text-secondary">
                        {c.creditReason ? `${c.creditReason} · ` : ""}
                        <span className="text-money text-text-primary">
                          {formatMoney(c.grossCents)}
                        </span>
                      </span>
                    </div>
                  ))}
                  <div className="flex justify-between text-sm border-t border-border-subtle pt-1.5 text-text-primary">
                    <span>Bereits gutgeschrieben (brutto)</span>
                    <span className="text-money">
                      {formatMoney(inv.creditGrossCents)}
                    </span>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-text-tertiary">Keine Gutschrift.</p>
              )}
              {inv.creditSurplusCents > 0 && (
                <div className="rounded-md bg-warning/10 border border-warning/30 p-3 space-y-2">
                  <div className="text-sm text-text-primary">
                    <strong>
                      Guthaben des Kunden: {formatMoney(inv.creditSurplusCents)}
                    </strong>{" "}
                    — bezahlt wurde mehr, als nach den Gutschriften fällig ist.
                    Rückzahlung hier erfassen (oder das Guthaben mit einer neuen
                    Rechnung verrechnen und dort als Zahlung vermerken).
                  </div>
                  <div className="flex items-end gap-2">
                    <Input
                      label="Erstattet (€)"
                      inputMode="decimal"
                      value={refundAmount}
                      onChange={(e) => setRefundAmount(e.target.value)}
                      placeholder={(inv.creditSurplusCents / 100).toFixed(2)}
                    />
                    <Button
                      variant="secondary"
                      loading={refund.isPending}
                      onClick={() => {
                        const cents = Math.round(
                          parseFloat(
                            (
                              refundAmount ||
                              String(inv.creditSurplusCents / 100)
                            ).replace(",", "."),
                          ) * 100,
                        );
                        if (!cents || cents <= 0) return;
                        refund.mutate({
                          invoiceId: id!,
                          date: new Date(),
                          amountCents: cents,
                        });
                      }}
                    >
                      Erstattung erfassen
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        <div className="grid grid-cols-4 gap-4 max-w-5xl">
          <CustomerCombobox
            value={customerId}
            onChange={(id) => {
              setCustomerId(id);
              setPropertyId("");
              setShowSiteAddress(false);
              setOrderId("");
            }}
          />
          <div className="col-span-2 flex items-end gap-4">
            <div className="flex-1 min-w-0">
              <Select
                label="Objekt (optional)"
                value={propertyId}
                onChange={(e) => {
                  setPropertyId(e.target.value);
                  // Durchführungsort gleich mit einblenden, sobald ein Objekt gewählt ist (abwählbar).
                  setShowSiteAddress(!!e.target.value);
                }}
                disabled={!customerId}
                options={[
                  {
                    value: "",
                    label: customerId ? "— kein Objekt —" : "erst Kunde wählen",
                  },
                  ...(properties.data ?? []).map((p) => ({
                    value: p.id,
                    label: p.label,
                  })),
                ]}
              />
            </div>
            <label
              className={`flex items-center gap-2 text-xs leading-tight max-w-[11rem] shrink-0 pb-2.5 ${propertyId ? "text-text-secondary" : "text-text-tertiary"}`}
            >
              <input
                type="checkbox"
                checked={showSiteAddress}
                disabled={!propertyId}
                onChange={(e) => setShowSiteAddress(e.target.checked)}
              />
              Durchführungsort im Adressblock anzeigen
            </label>
          </div>
          <Select
            label="Auftrag (optional)"
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
            disabled={!customerId}
            options={[
              {
                value: "",
                label: customerId ? "— kein Auftrag —" : "erst Kunde wählen",
              },
              ...(orders.data ?? []).map((o) => ({
                value: o.id,
                label: `${o.number ?? "ohne Nr."} · ${o.title}`,
              })),
            ]}
          />
          <div className="col-span-full">
            <Textarea
              label="Betreff (mehrzeilig möglich — erscheint im PDF unter dem Dokumenttitel)"
              rows={3}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="col-span-full">
            <Textarea
              label="Info / Notiz (erscheint im PDF unter dem Betreff — leer = nicht angezeigt)"
              rows={2}
              value={infoText}
              onChange={(e) => setInfoText(e.target.value)}
            />
          </div>
          <Input
            label="Rechnungsdatum"
            type="date"
            value={issueDate}
            onChange={(e) => setIssueDate(e.target.value)}
          />
          <Input
            label="Fällig bis"
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
          />
          <Select
            label="Leistung"
            value={serviceMode}
            onChange={(e) =>
              setServiceMode(e.target.value as "date" | "period")
            }
            options={[
              { value: "date", label: "Durchführung am" },
              { value: "period", label: "Leistungszeitraum" },
            ]}
          />
          {serviceMode === "date" ? (
            <Input
              label="Durchführung am"
              type="date"
              value={serviceDate}
              onChange={(e) => setServiceDate(e.target.value)}
            />
          ) : (
            <>
              <Input
                label="Zeitraum von"
                type="date"
                value={periodFrom}
                onChange={(e) => setPeriodFrom(e.target.value)}
              />
              <Input
                label="Zeitraum bis"
                type="date"
                value={periodTo}
                onChange={(e) => setPeriodTo(e.target.value)}
              />
            </>
          )}
          <Select
            label="USt-Satz"
            value={String(taxRate)}
            onChange={(e) => setTaxRate(Number(e.target.value))}
            options={[
              { value: "20", label: "20 %" },
              { value: "13", label: "13 %" },
              { value: "10", label: "10 %" },
              { value: "0", label: "0 %" },
            ]}
          />
          <label className="col-span-2 flex items-start gap-2 text-sm text-text-secondary pb-1.5 self-end">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={rcBau}
              onChange={(e) => setRcBau(e.target.checked)}
            />
            <span>
              Reverse Charge – Steuerschuldnerschaft des Leistungsempfängers
              (§&nbsp;19 Abs.&nbsp;1a UStG)
              <span className="block text-xs text-text-tertiary">
                B2B-Bauleistung: Rechnung ohne USt; der vollständige
                Pflichthinweis wird automatisch aufgedruckt.
              </span>
            </span>
          </label>
        </div>

        {rcBau && (
          <div className="max-w-5xl -mt-2 rounded-md border border-border-subtle bg-bg-tertiary px-3 py-2 text-xs text-text-secondary">
            Auf der Rechnung erscheint: „Steuerschuldnerschaft des
            Leistungsempfängers (Reverse Charge) gemäß § 19 Abs. 1a UStG. Die
            Umsatzsteuer ist vom Rechnungsempfänger abzuführen."
            <span className="text-text-tertiary">
              {" "}
              Der Wortlaut ist unter Einstellungen → Standardtexte anpassbar.
            </span>
          </div>
        )}

        <div className="grid gap-4 max-w-5xl">
          <Textarea
            label="Einleitungstext"
            rows={2}
            value={introText}
            onChange={(e) => setIntroText(e.target.value)}
          />
        </div>

        <div className="max-w-5xl">
          <h2 className="text-sm font-semibold text-text-primary mb-2">
            Positionen
          </h2>
          <PositionEditor rows={rows} onChange={setRows} showModes={false} />
        </div>

        <div className="max-w-md ml-auto card p-4 space-y-1.5 text-sm">
          {rcBau ? (
            <>
              <SumRow
                label="Nettobetrag"
                value={formatMoney(totals.netCents)}
              />
              <SumRow
                label="USt (Reverse Charge)"
                value={formatMoney(0)}
                muted
              />
              <SumRow
                label="Gesamtbetrag netto"
                value={formatMoney(totals.netCents)}
                bold
              />
            </>
          ) : (org.data as any)?.taxScheme !== "kleinunternehmer" ? (
            <>
              <SumRow
                label="Nettobetrag"
                value={formatMoney(totals.netCents)}
              />
              <SumRow
                label={`USt ${taxRate} %`}
                value={formatMoney(totals.taxCents)}
              />
              <SumRow
                label="Rechnungsbetrag brutto"
                value={formatMoney(totals.grossCents)}
                bold
              />
            </>
          ) : (
            <SumRow
              label="Rechnungsbetrag"
              value={formatMoney(totals.netCents)}
              bold
            />
          )}
          {inv && inv.deductionGrossCents > 0 && (
            <>
              <SumRow
                label="abzügl. Teilrechnungen"
                value={`− ${formatMoney(inv.deductionGrossCents)}`}
                muted
              />
              <SumRow
                label="Restbetrag"
                value={formatMoney(inv.payableGrossCents)}
                bold
              />
            </>
          )}
          {inv && inv.paidCents > 0 && (
            <>
              <SumRow
                label="bereits bezahlt"
                value={formatMoney(inv.paidCents)}
                muted
              />
              <SumRow label="offener Betrag" value={formatMoney(open)} bold />
            </>
          )}
          {materialCostCents > 0 && (
            <div className="border-t border-border-subtle pt-1.5 mt-1.5">
              <SumRow
                label="Materialkosten (Einkauf netto, nur intern)"
                value={formatMoney(materialCostCents)}
                muted
              />
            </div>
          )}
        </div>

        {/* Zahlungsplan */}
        <div className="max-w-3xl">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold text-text-primary">
              Zahlungsplan (optional)
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                setSched([
                  ...sched,
                  { label: "", basis: "percent", value: 30, note: "" },
                ])
              }
            >
              <Plus className="w-3.5 h-3.5" /> Zeile
            </Button>
          </div>
          {sched.map((s, i) => (
            <div
              key={i}
              className="grid grid-cols-[1fr_110px_90px_1fr_32px] gap-2 mb-2 items-center"
            >
              <input
                className="bg-bg-input border border-border rounded px-2 py-1.5 text-sm"
                placeholder="z. B. Anzahlung bei Auftrag"
                value={s.label}
                onChange={(e) =>
                  setSched(
                    sched.map((x, j) =>
                      j === i ? { ...x, label: e.target.value } : x,
                    ),
                  )
                }
              />
              <select
                className="bg-bg-input border border-border rounded px-2 py-1.5 text-sm"
                value={s.basis}
                onChange={(e) =>
                  setSched(
                    sched.map((x, j) =>
                      j === i
                        ? { ...x, basis: e.target.value as "percent" | "fixed" }
                        : x,
                    ),
                  )
                }
              >
                <option value="percent">Prozent</option>
                <option value="fixed">Betrag (Cent)</option>
              </select>
              <input
                type="number"
                className="bg-bg-input border border-border rounded px-2 py-1.5 text-sm text-right"
                value={s.value}
                onChange={(e) =>
                  setSched(
                    sched.map((x, j) =>
                      j === i
                        ? { ...x, value: Number(e.target.value) || 0 }
                        : x,
                    ),
                  )
                }
              />
              <input
                className="bg-bg-input border border-border rounded px-2 py-1.5 text-sm"
                placeholder="Notiz"
                value={s.note}
                onChange={(e) =>
                  setSched(
                    sched.map((x, j) =>
                      j === i ? { ...x, note: e.target.value } : x,
                    ),
                  )
                }
              />
              <button
                className="text-text-tertiary hover:text-danger"
                onClick={() => setSched(sched.filter((_, j) => j !== i))}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>

        <div className="max-w-5xl card p-4 space-y-3">
          <p className="text-sm font-semibold text-text-primary">Konditionen</p>
          <Select
            label="SachbearbeiterIn"
            value={handlerUserId}
            onChange={(e) => setHandlerUserId(e.target.value)}
            options={[
              { value: "", label: "— keine —" },
              ...(members.data ?? []).map((m) => ({
                value: m.userId,
                label: m.name,
              })),
            ]}
          />
          <div className="grid grid-cols-3 gap-4">
            <Input
              label="Skonto %"
              type="number"
              min={0}
              max={100}
              step="0.5"
              value={skontoPercent}
              onChange={(e) =>
                setSkontoPercent(parseFloat(e.target.value) || 0)
              }
            />
            <Input
              label="Skonto-Frist (Tage)"
              type="number"
              min={0}
              value={skontoDays}
              onChange={(e) => setSkontoDays(parseInt(e.target.value) || 0)}
            />
            <Input
              label="ohne Abzug (Tage)"
              type="number"
              min={0}
              value={paymentGraceDays}
              onChange={(e) =>
                setPaymentGraceDays(parseInt(e.target.value) || 0)
              }
            />
          </div>
          <p className="text-xs text-text-tertiary">
            0 % blendet die Skonto-Zeile aus. Skonto rechnet vom Bruttobetrag.
          </p>
        </div>

        <div className="grid gap-4 max-w-5xl">
          <Textarea
            label="Schlusstext"
            rows={2}
            value={closingText}
            onChange={(e) => setClosingText(e.target.value)}
          />
          <Textarea
            label="AGB-/Gerichtsstandshinweis"
            rows={2}
            value={legalNoteText}
            onChange={(e) => setLegalNoteText(e.target.value)}
          />
          <Textarea
            label="Gerichtsstand"
            rows={2}
            value={courtVenueText}
            onChange={(e) => setCourtVenueText(e.target.value)}
          />
        </div>

        {/* Zahlungseingänge — bei einer Gutschrift schuldet der Kunde nichts, daher nicht relevant */}
        {!isNew && inv?.kind !== "credit_note" && (
          <div className="max-w-3xl">
            <h2 className="text-sm font-semibold text-text-primary mb-2">
              Zahlungseingänge
            </h2>
            {inv?.payments.length ? (
              <div className="space-y-1.5 mb-3">
                {inv.payments.map((p) => (
                  <div
                    key={p.id}
                    className="flex items-center justify-between card p-2.5 text-sm"
                  >
                    <span className="text-text-secondary">
                      {formatDate(p.date as unknown as string)} · {p.method}
                      {p.reference ? ` · ${p.reference}` : ""}
                    </span>
                    <span className="flex items-center gap-3">
                      <span className="text-money text-text-primary">
                        {formatMoney(p.amountCents)}
                      </span>
                      <button
                        className="text-text-tertiary hover:text-danger"
                        onClick={() =>
                          removePayment.mutate({
                            invoiceId: id!,
                            paymentId: p.id,
                          })
                        }
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-text-tertiary mb-3">
                Noch keine Zahlung erfasst.
              </p>
            )}
            <div className="flex items-end gap-2 flex-wrap">
              <Input
                label="Datum"
                type="date"
                value={payDate}
                onChange={(e) => setPayDate(e.target.value)}
              />
              <Input
                label="Betrag (€)"
                inputMode="decimal"
                value={payAmount}
                onChange={(e) => setPayAmount(e.target.value)}
                placeholder={(open / 100).toFixed(2)}
              />
              <Select
                label="Art"
                value={payMethod}
                onChange={(e) => setPayMethod(e.target.value)}
                options={[
                  { value: "transfer", label: "Überweisung" },
                  { value: "card", label: "Karte" },
                  { value: "cash", label: "Bar" },
                ]}
              />
              <Button
                variant="secondary"
                loading={addPayment.isPending || addPaymentSkonto.isPending}
                onClick={() => {
                  const cents = Math.round(
                    parseFloat(payAmount.replace(",", ".")) * 100,
                  );
                  if (!cents || cents <= 0) return;
                  if (paySkonto) {
                    addPaymentSkonto.mutate({
                      invoiceId: id!,
                      date: new Date(payDate),
                      amountCents: cents,
                      method: payMethod as "transfer" | "card" | "cash",
                    });
                  } else {
                    addPayment.mutate({
                      invoiceId: id!,
                      date: new Date(payDate),
                      amountCents: cents,
                      method: payMethod as "transfer" | "card" | "cash",
                    });
                  }
                }}
              >
                Erfassen
              </Button>
              {!!inv?.skontoPercent && (
                <label className="flex items-center gap-1.5 text-xs text-text-secondary pb-2.5">
                  <input
                    type="checkbox"
                    checked={paySkonto}
                    onChange={(e) => setPaySkonto(e.target.checked)}
                  />
                  mit {inv.skontoPercent} % Skonto-Abzug — gilt dann als
                  vollständig bezahlt
                </label>
              )}
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Rechnung löschen?"
        message="Nur Entwürfe ohne Nummer können gelöscht werden."
        confirmLabel="Löschen"
        danger
        onConfirm={() => remove.mutate({ id: id! })}
      />
      <Dialog
        open={confirmCancel}
        onOpenChange={setConfirmCancel}
        title="Rechnung stornieren?"
        description="Die Rechnung selbst bleibt unverändert einsehbar."
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmCancel(false)}>
              Abbrechen
            </Button>
            <Button
              variant="danger"
              loading={cancel.isPending}
              onClick={() => cancel.mutate({ id: id!, reason: cancelReason })}
            >
              Stornieren
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-text-secondary">
            Es wird automatisch eine <strong>Storno-Gutschrift</strong> über den
            noch nicht gutgeschriebenen Betrag mit eigener Nummer (GS-…) und
            Bezug auf diese Rechnung ausgestellt; die Rechnung erhält den Status
            „storniert". Bei Bedarf danach eine neue, richtige Rechnung anlegen.
            Nur einen Teil zurücknehmen? Dann stattdessen „Gutschrift
            erstellen".
          </p>
          <Textarea
            label="Grund (erscheint auf der Gutschrift)"
            rows={2}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="z. B. Rechnung fehlerhaft"
          />
        </div>
      </Dialog>
      {inv && inv.number && (
        <CreditNoteDialog
          open={creditOpen}
          onOpenChange={setCreditOpen}
          invoiceId={id!}
          invoiceNumber={inv.number}
          items={inv.items}
          remaining={inv.creditRemaining}
          onCreated={(gsId) => {
            utils.invoice.get.invalidate({ id: id! });
            utils.invoice.list.invalidate();
            leave(`/rechnungen/${gsId}`);
          }}
        />
      )}
    </div>
  );
}

function SumRow({
  label,
  value,
  bold,
  muted,
}: {
  label: string;
  value: string;
  bold?: boolean;
  muted?: boolean;
}) {
  return (
    <div
      className={`flex justify-between ${bold ? "text-base font-semibold text-text-primary border-t border-border-subtle pt-2" : muted ? "text-xs text-text-tertiary" : "text-text-secondary"}`}
    >
      <span>{label}</span>
      <span className="text-money">{value}</span>
    </div>
  );
}
